import { useEffect, useState } from "react";
import { KeyRound, Mail, Loader2, ShieldCheck, HeartPulse, LogOut, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePrefs } from "@/hooks/useCollection";
import { Button } from "@/components/ui/button";
import { Section } from "@/components/duitku/shared";
import { MascotBubble } from "@/components/Mascot";
import { TokenUsage } from "@/components/TokenUsage";

export const ChangePin = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [target, setTarget] = useState(user.id);
  const [email, setEmail] = useState("");
  const [step, setStep] = useState("request");
  const [code, setCode] = useState("");
  const [pin, setPin] = useState("");
  const [pin2, setPin2] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data));
    api.get("/pin/info").then((r) => setEmail(r.data.admin_email)).catch(() => setEmail("(gagal dimuat)"));
  }, []);

  const run = async (fn) => {
    setBusy(true);
    try { await fn(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const request = () => run(async () => {
    const { data } = await api.post("/pin/request", { target_user_id: target });
    toast.success(`Kode dikirim ke ${data.sent_to}`);
    setStep("confirm");
  });

  const confirm = () => run(async () => {
    if (pin !== pin2) throw { response: { data: { detail: "Konfirmasi PIN tidak sama" } } };
    const { data } = await api.post("/pin/confirm", { code, new_pin: pin });
    toast.success(`PIN ${data.user} berhasil diganti!`);
    setStep("request"); setCode(""); setPin(""); setPin2("");
  });

  const targetUser = users.find((u) => u.id === target);
  const digits = (setter) => (e) => setter(e.target.value.replace(/\D/g, "").slice(0, 6));

  return (
    <Section title={<span className="flex items-center gap-2"><KeyRound className="w-5 h-5 text-pink-400" /> Ganti PINku</span>}>
      <p className="text-sm text-gray-500 flex items-center gap-2 mb-4"><Mail className="w-4 h-4 text-sky-400" /> Kode verifikasi dikirim ke email admin: <strong data-testid="settings-admin-email">{email}</strong></p>
      {user.role === "master" && users.length > 1 && (
        <div className="flex gap-2 mb-4">
          {users.map((u) => <button key={u.id} disabled={step === "confirm"} onClick={() => setTarget(u.id)} className={`chip ${target === u.id ? "chip-on" : "chip-off"}`} data-testid={`settings-target-${u.role}`}>{u.name}</button>)}
        </div>
      )}
      {step === "request" ? (
        <Button onClick={request} disabled={busy} className="rounded-full h-11" data-testid="settings-change-pin-btn">
          {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Kirim kode verifikasi
        </Button>
      ) : (
        <div className="space-y-3 max-w-sm">
          <input className="field tracking-[0.4em] text-center font-semibold" inputMode="numeric" placeholder="Kode 6 digit" value={code} onChange={digits(setCode)} data-testid="settings-otp-input" />
          <input className="field" type="password" inputMode="numeric" placeholder={targetUser?.role === "master" ? "PIN baru (6 digit)" : "PIN baru (4-6 digit)"} value={pin} onChange={digits(setPin)} data-testid="settings-new-pin-input" />
          <input className="field" type="password" inputMode="numeric" placeholder="Ulangi PIN baru" value={pin2} onChange={digits(setPin2)} data-testid="settings-new-pin-confirm-input" />
          <div className="flex gap-2">
            <Button onClick={confirm} disabled={busy || code.length !== 6 || pin.length < 4} className="rounded-full h-11" data-testid="settings-verify-otp-btn">
              {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Simpan PIN baru
            </Button>
            <Button variant="ghost" className="rounded-full h-11" onClick={() => setStep("request")} data-testid="settings-cancel-btn">Batal</Button>
          </div>
        </div>
      )}
    </Section>
  );
};

export const AdminEmail = () => {
  const [current, setCurrent] = useState("");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState("request");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/pin/info").then((r) => setCurrent(r.data.admin_email)).catch(() => setCurrent("(gagal dimuat)")); }, []);
  const run = async (fn) => {
    setBusy(true);
    try { await fn(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const request = () => run(async () => {
    const { data } = await api.post("/admin/email/request", { email });
    toast.success(`Kode dikirim ke ${data.sent_to}`);
    setStep("confirm");
  });
  const confirm = () => run(async () => {
    const { data } = await api.post("/admin/email/confirm", { code });
    setCurrent(data.admin_email);
    toast.success("Email admin berhasil diganti!");
    setStep("request"); setEmail(""); setCode("");
  });
  return (
    <Section title={<span className="flex items-center gap-2"><Mail className="w-5 h-5 text-sky-400" /> Email adminku</span>}>
      <p className="text-sm text-gray-500 mb-3">Email admin sekarang: <strong data-testid="admin-email-current">{current}</strong>. Kode ganti PIN dikirim ke sini.</p>
      {step === "request" ? (
        <div className="flex flex-col sm:flex-row gap-2 max-w-lg">
          <input className="field" type="email" placeholder="Email admin baru" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email-input" />
          <Button onClick={request} disabled={busy || !email.includes("@")} className="rounded-full h-11 shrink-0" data-testid="admin-email-request-btn">
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Kirim kode ke email baru
          </Button>
        </div>
      ) : (
        <div className="space-y-3 max-w-sm">
          <p className="text-xs text-gray-500">Cek kotak masuk <strong>{email}</strong>, lalu masukkan kodenya.</p>
          <input className="field tracking-[0.4em] text-center font-semibold" inputMode="numeric" placeholder="Kode 6 digit" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} data-testid="admin-email-otp-input" />
          <div className="flex gap-2">
            <Button onClick={confirm} disabled={busy || code.length !== 6} className="rounded-full h-11" data-testid="admin-email-confirm-btn">Simpan email baru</Button>
            <Button variant="ghost" className="rounded-full h-11" onClick={() => setStep("request")} data-testid="admin-email-cancel-btn">Batal</Button>
          </div>
        </div>
      )}
    </Section>
  );
};

const WEEKDAYS = [[1, "Senin"], [2, "Selasa"], [3, "Rabu"], [4, "Kamis"], [5, "Jumat"], [6, "Sabtu"], [0, "Minggu"]];

const PiketPrefs = () => {
  const { prefs, save } = usePrefs();
  if (!prefs) return null;
  const cur = prefs.piket_weekly || [];
  const toggle = async (n) => {
    try {
      await save({ piket_weekly: cur.includes(n) ? cur.filter((x) => x !== n) : [...cur, n] });
      toast.success("Jadwal piket tersimpan");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Section title={<span className="flex items-center gap-2"><CalendarClock className="w-5 h-5 text-amber-400" /> Jadwal piketku</span>}>
      <p className="text-sm text-gray-500 mb-3">Pilih hari piket rutin tiap minggu (default Minggu). Hari piket tidak bisa diambil libur.</p>
      <div className="flex gap-2 flex-wrap">
        {WEEKDAYS.map(([n, l]) => (
          <button key={n} onClick={() => toggle(n)} className={`chip ${cur.includes(n) ? "!bg-amber-400 !border-amber-400 text-white" : "chip-off"}`} data-testid={`settings-piket-${n}`}>{l}</button>
        ))}
      </div>
    </Section>
  );
};

const CyclePrefs = () => {
  const { prefs, save } = usePrefs();
  const [v, setV] = useState(null);
  useEffect(() => {
    if (prefs) setV({ cycle_length: prefs.cycle_length, period_length: prefs.period_length, off_quota: prefs.off_quota ?? 3 });
  }, [prefs]);
  if (!v) return null;
  return (
    <Section title={<span className="flex items-center gap-2"><HeartPulse className="w-5 h-5 text-rose-400" /> Jadwal kerja & siklusku</span>}>
      <p className="text-sm text-gray-500 mb-3">Semua hari dihitung hari kerja, kecuali jatah libur bulanan yang kamu pilih di kalender Mood Aku. Siklus dipakai untuk prediksi sampai ada cukup catatan mens.</p>
      <div className="grid grid-cols-3 gap-3 max-w-lg">
        <label className="text-xs font-medium text-pink-600 space-y-1"><span>Jatah libur / bulan (hari)</span><input className="field" type="number" min="0" value={v.off_quota} onChange={(e) => setV({ ...v, off_quota: e.target.value })} data-testid="settings-off-quota" /></label>
        <label className="text-xs font-medium text-pink-600 space-y-1"><span>Panjang siklus (hari)</span><input className="field" type="number" value={v.cycle_length} onChange={(e) => setV({ ...v, cycle_length: e.target.value })} data-testid="settings-cycle-length" /></label>
        <label className="text-xs font-medium text-pink-600 space-y-1"><span>Lama mens (hari)</span><input className="field" type="number" value={v.period_length} onChange={(e) => setV({ ...v, period_length: e.target.value })} data-testid="settings-period-length" /></label>
      </div>
      <Button variant="secondary" className="rounded-full mt-4" data-testid="settings-cycle-save" onClick={async () => { try { await save({ cycle_length: Number(v.cycle_length), period_length: Number(v.period_length), off_quota: Number(v.off_quota) }); toast.success("Tersimpan"); } catch (e) { toast.error(errMsg(e)); } }}>Simpan</Button>
    </Section>
  );
};

export default function SettingsPage() {
  const { user, logout } = useAuth();
  return (
    <div className="space-y-5">
      <div>
        <p className="text-sm text-pink-500 font-medium">Akunku & pengaturan</p>
        <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-gray-800">Pengaturan</h1>
      </div>
      <MascotBubble pose="happy" testid="settings-role-info">
        Kamu login sebagai <strong>{user.name}</strong> {user.role === "master" ? <span className="inline-flex items-center gap-1 text-amber-500"><ShieldCheck className="w-4 h-4" /> admin utama</span> : "(user)"}.
        {user.role === "master" ? " Kamu bisa mengganti PIN semua akun." : " Ganti PIN butuh kode dari email admin."}
      </MascotBubble>
      <ChangePin />
      {user.role === "master" && <AdminEmail />}
      <TokenUsage />
      <PiketPrefs />
      <CyclePrefs />
      <Button variant="outline" onClick={logout} className="rounded-full border-pink-200 text-pink-600" data-testid="settings-logout-btn"><LogOut className="w-4 h-4 mr-2" /> Keluar</Button>
    </div>
  );
}
